import {
  ApprovalTask,
  Batch,
  MobileApprovalLoop,
  ShiftDates,
  ShiftRates,
  SummaryAccountCode,
  SummaryAccountCodes,
  ValidationError,
  ValueError
} from '../timecard.model';
import {
  IBaseBreakage,
  IBaseBreakageOverride,
  IBaseWorkDetail,
  IBreakageTotals,
  IPreviousSummaryValues,
  ISummaryDate,
  ISummaryDates,
  ISummaryDetail,
  ISummaryDetails,
  ISummaryJobDetail,
  ISummaryJobDetails,
  ISummaryPayDetail,
  ISummaryPayDetails
} from './common.model';
import {IBaseFlatShift} from './generic.model';
import {BaseUnitShift, ShootingCrew, Unit, UnitScheduleType} from '../unit.model';
import {UkStartcard} from '../startcard-models/uk.model';
import {KeyedById} from '../utilities/utility.types.model';
import {AccountCode, Punches} from '../timecard.shifts.model';
import {CustomSickPayCode} from '../project-setting.model';
import {ApiInvoiceStatus} from '../paymaster-batch.model';
import {GrossPay} from '../htg-timecard.submodel';
import {TimecardStatus, TimecardTypes, WorkflowApprovalStatus} from '../timecard.enum';
import {CalculationWaivers} from '../start-card.model';

/**
 * UK Timecard/Shifts Specific Enums
 * NOTE: All Enums should be defined in the order for display in UI, so that it would be possible to ngFor the enum using keyvalue pipe.
 *  e.g. *ngFor="let enum of UkPayType | keyvalue"
 * */
export enum UkPayType {
  BLANK = '', // TODO: We are adding a "Blank" option so that we can have a Default value instead of undefined/null
  FLAT = 'Flat Day',
  HALF = 'Half Day',
  ACTUAL = 'ACT',
  NO_PAY = 'No Pay',
}
export enum UkDayTypeDesc {
  WORKED = 'Worked',
  HOL_NW = 'Bank Hol NW',
  HOL_WKD = 'Bank Hol Wkd',
  CANCELLATION = 'Cancellation',
  HOLIDAY = 'Holiday',
  OTHER = 'Other',
  SICK = 'Sick',
  REST = 'Rest',
  TRAVEL = 'Travel',
  TURN_AROUND = 'Turnaround',
  DELETE = 'Delete', // This is basically NOT_WORKED
  // HOLD = 'Hold', // Commented out - not valid for UK (STF-35600)
}
export enum UkDayType {
  WORKED = 'WORKED',
  HOL_NW = 'HOL_NW',
  HOL_WKD = 'HOL_WKD',
  CANCELLATION = 'CANCELLATION',
  HOLIDAY = 'HOLIDAY',
  OTHER = 'OTHER',
  SICK = 'SICK',
  REST = 'REST',
  TRAVEL = 'TRAVEL',
  TURN_AROUND = 'TURN_AROUND',
  DELETE = 'DELETE',
  // HOLD = 'HOLD', // Commented out - not valid for UK (STF-35600)
}

export enum UkDayType2 {
  '6TH' = '6th Day',
  // '6TH_1X' = '6th Day (1x)',
  '7TH' = '7th Day',
}

// (STF-28822)UkDayType1Display & UkDayType2Display created to accommodate AC 6(c and d), 7(c). Display value changes for BHNW, BHWK and 6TH_1X.
export enum UkDayType1Display {
  WORKED = 'Worked',
  HOL_NW = 'BHNW',
  HOL_WKD = 'BHWK',
  CANCELLATION = 'Cancellation',
  HOLIDAY = 'Holiday',
  OTHER = 'Other',
  SICK = 'Sick',
  REST = 'Rest',
  TRAVEL = 'Travel',
  TURN_AROUND = 'Turnaround',
  DELETE = 'Delete',
  // HOLD = 'Hold', // Commented out - not valid for UK (STF-35600)
}

export enum UkNoTimeDayType {
  HOL_NW = 'BHNW',
  REST = 'Rest',
  TURN_AROUND = 'Turnaround',
  CANCELLATION = 'Cancellation',
  SICK = 'Sick',
  HOLIDAY = 'Holiday'
}

export enum UkDayType2Display {
  '6TH' = '6th Day',
  '6TH_1X' = '6th (1x)',
  '7TH' = '7th Day',
}

export enum PayLineStatus {
  PENDING = 'PENDING',
  READY_FOR_PAYROLL = 'READY FOR PAYROLL',
  SENT_TO_PAYROLL = 'SENT TO PAYROLL'
}

export enum ApiTimecardPayrollStatus {
  PENDING = 'PENDING',
  PARTIALLY_SENT = 'PARTIALLY_SENT',
  SENT_TO_PAYROLL = 'SENT_TO_PAY_ROLL'
}

export enum TimecardPayrollStatus {
  PENDING = 'PENDING',
  PARTIALLY_SENT = 'PARTIALLY SENT',
  SENT_TO_PAYROLL = 'SENT TO PAYROLL'
}

/**
 * This file will define the UK Region Specific Timecard Model and all the Sub Types used by the Timecard interface
 * */
// region Timecard
export interface UkTimecard {
  approvalReroute: boolean;
  approvalTasks: ApprovalTask[];
  batch: Batch;
  breakWeeklyAsDaily: boolean;
  breakageTotals: UkBreakageTotals;
  createdBy: string;
  createdTimestamp: string;
  customSickPayCode: CustomSickPayCode;
  errors?: ValidationError[];
  hasConsequentialChanges?: boolean;
  holidayPayCodeGroup: string;
  invoiceStatus?: ApiInvoiceStatus;
  isApplyOtherDataSource: boolean;
  isDailyTimecard?: boolean;
  isDisplayOrangeCircle: boolean;
  isInternal: boolean;
  // isLayoffTimecard: boolean;
  isMobileApproved: boolean;
  isMobileTimecard?: boolean;
  isRevised: boolean;
  isUnsubmittable: boolean;
  isWeekendRestOverride: boolean;  // TODO: isWeekendRestOverride do we need this in UK?
  mobileApprovalLoops?: MobileApprovalLoop[];
  modifiedTimestamp: string;
  processDefinitionId?: string;
  processInstanceId?: string;
  rawBreakages?: GrossPay;
  revisedBy: string;
  revisedTimestamp: string;
  shifts: UkShifts;
  startcard: UkStartcard;
  status: TimecardStatus;
  summaryItems: UkSummaryItems;
  timecardId: string;
  timecardType: TimecardTypes;
  weekEndingDate: string;
  weeklyMobileTimeId?: string;
  workflowApprovalStatus: WorkflowApprovalStatus;
  payrollStatus: TimecardPayrollStatus;
}
// endregion Timecard

// region Shifts
/**
 * UkShifts
 * example
 * const testUkShifts: UkShifts = {
 *   shiftDates: JSON.parse(`{}`),
 *   shiftRates: JSON.parse(`{}`),
 *   accountCodes: JSON.parse(`{}`),
 *   punches: JSON.parse(`{}`),
 *   breakages: JSON.parse(`{}`),
 *   workDetails: JSON.parse(`{}`),
 *   shiftUnits: JSON.parse(`{}`)
 * };
 * */
export interface UkShifts {
  shiftDates: ShiftDates;
  shiftRates: ShiftRates;
  workDetails: UkCrewWorkDetails;
  punches: Punches<string>;
  accountCodes: KeyedById<AccountCode>;
  breakages: UkBreakages;
  shiftUnits: ShiftUnits;
}
// endregion Shifts

// region --Shifts Sub Models--
// region ShiftUnits
/**
 * ShiftUnits
 * This is the Unit Section on the Punch Grid.  None of the fields are required.
 * We are using the ShiftUnit object to hold on to the Unit data used for HTG calculation for that shift.
 * TODO - Tech Requirement - If ShiftUnit.unitId is not empty, and the timecard has been calculated, then htgCalcUnitShift should not be empty.
 *  I'm not sure how we can enforce this to prevent issues down the line...
 * */
export interface ShiftUnit {
  shiftId: string;
  unitId?: ValueError<string>;
  unit?: Unit;
  phase?: ValueError<string>; // Prep/Shoot/Wrap
  shootCrew?: ValueError<ShootingCrew | ''>;
  scheduleType?: ValueError<UnitScheduleType | ''>;
  // SnapShot value of ShiftUnit used for HTG Calc
  htgCalcUnitShift?: BaseUnitShift;
}
export type ShiftUnits = KeyedById<ShiftUnit>;
// endregion ShiftUnits
// region WorkDetails
/**
 * IUkCrewWorkDetail
 * There are no UK SAG yet, so all we need is a IUkCrewWorkDetail.  When we actually have to do UK SAG, then we'll refactor IUkCrewWorkDetail.
 * */
interface IUkCrewWorkDetail extends IBaseWorkDetail {
  dayType1: ValueError<UkDayType | ''>;
  payType: ValueError<UkPayType>;
}
/**
 * IUkTimecardOverrides
 * TODO: finialize https://epglobal.atlassian.net/wiki/spaces/TT/pages/251134778/UK+-+SC+-+Updated+Layout+and+Functionality
 * Camera Overtime – drop down with values:  Yes  (default), No
 * Non-Camera Overtime – drop down with values:  Yes  (default), No
 * Broken Turnaround – drop down with values:  Yes  (default), No
 * Early Call – drop down with values:  Yes  (default), No
 * Pre-Call – drop down with values:  Yes  (default), No
 * De-Rig – drop down with values:  Yes  (default), No
 * Night Work – drop down with values:  Yes  (default), No
 * Delayed Meal Break – drop down with values:  Yes  (default), No
 * Curtailed Meal Break – drop down with values:  Yes  (default), No
 * Excess Travel – drop down with values:  Yes  (default), No
 * */

export type UkCrewWorkDetail = IUkCrewWorkDetail & CalculationWaivers;
/**
 * UkCrewWorkDetails
 * EXAMPLE
 * const testUkCrewWorkDetails: UkCrewWorkDetails = {
 *   'ca5edbc2-6b78-4bac-b8b7-c337824dbf91': {
 *     brokenTurnaround: false,
 *     cameraOvertime: false,
 *     curtailedMealBreak: false,
 *     dayType1: {value: ''},
 *     dayType2: {value: ''},
 *     delayedMealBreak: false,
 *     derig: false,
 *     earlyCall: false,
 *     excessTravel: false,
 *     nightWork: false,
 *     nonCameraOvertime: false,
 *     payType: {value: ''},
 *     preCall: false,
 *     shiftId: '123',
 *   }
 * };
 * */
// TODO not sure if KeyedById works in this case as UkCrewWorkDetails should inherit from IBaseWorkDetails, it essentially is, but not sure if they are the same
export type UkCrewWorkDetails = KeyedById<UkCrewWorkDetail>;
// endregion WorkDetails
// region Breakages
/**
 * UK Pay Factor
 * TODO: this is TBD, I still don't understand what we are doing in Pay Factor in the Breakage Section, as well as what is needed fro BO and SplitGL
 *   Technically, we should have a Common Type for PayFactor that compose PayFactor and BreakageOverride fields,
 * */
export interface IUkPayFactor {
  amount: number;
  breakageOrdinal: number; // this is the order in which the pay factor should be displayed in the Breakage Grid.
  // Note that system still need to display Wrk Hr and Amount Columns, pay factors will be in between WkrHr and Amount
  duration: ValueError<number>; // numberOfMins
  isFlat: boolean; // this is for FLAT Payment, e.g. Night Work (PayCode 201)
  payCode: string;  // this is the actual Pay Code, e.g. 110, 200, etc...
  payCodeName: string; // this is the display name of the pay code in the column, e.g. OT, BTA, EC, etc...
  payDescription: string; // this is for display hovering tool text.
  payFactor: number; // this is the PayCode's pay factor, e.g. 1, 1,5, 2.  This is null if isFlat = true. We choose null because if we calc with a Flat Pay Factor, we should get aNaN
  payFactorName: string; // this is the display Name of the pay Factor, e.g. 1x, 1.5x, FLAT, etc...
  rate?: number; // Hourly Rate, this is given by HTG. IT could be the Startcard Rate, but STF don't map it from startcard.
  effectiveRate?: number;
}

export interface IUkPayFactorBreakageOverride {
  originalDuration?: number; // numberOfMins
}
export interface UkPayFactors {
  [payCode: string]: IUkPayFactor & IUkPayFactorBreakageOverride; // the PayCode Key is the actual PayCode, not the Display name
}
export interface IUkBreakage extends IBaseBreakage, IBaseBreakageOverride {
  payFactors?: UkPayFactors;
  compensatoryRest: number; // number of minutes
/*// BTA, DMP and CMP are now part of PayFactors columns as they are duration as well
  brokenTurnaround: number; // number of minutes
  deferredMealPenalty: number; // amount??
  curtailedMealPenalty: number; // amount??
*/
  hasBrokenTurnaround: boolean; // for BTA indicator
  prepWrap: number; // Total Number of Minutes, UI can map this to hh:mm format
}
export type UkBreakages = KeyedById<IUkBreakage>;
// endregion Breakages

// endregion --Shifts Sub Models--

// region --SummaryItems--
export interface IUkSummaryPayDetail extends ISummaryPayDetail {
  units: ValueError<number>;
  invoice: string; // user entered invoice number for the line item
  payDescription: string; // system generated during Calculate (or Save), user can modify
  holidayPay: number;
  holidayPayPercentage: number;
  effectiveRateIncludingHoliday: number; // HTG will calculate the effective rate (Rate * PayFactor), while keeping track of the cap
  effectiveRateExcludingHoliday: number;
  amountIncludingHoliday: number;
  effectiveRate?: number; // HTG will calculate the effective rate (Rate * PayFactor), while keeping track of the cap
  payLineStatus: PayLineStatus;
}
export type IUkFlatSummaryLine = ISummaryDate & ISummaryDetail & IUkSummaryPayDetail & ISummaryJobDetail & SummaryAccountCode & {
  accountCodeString?: string; // Concatenated account code segments for sorting purposes
};

export interface UkSummaryItems {
  accountCodes: SummaryAccountCodes;
  basics: ISummaryDetails<ISummaryDetail>;
  dates: ISummaryDates;
  jobDetails: ISummaryJobDetails<ISummaryJobDetail>;
  payDetails: ISummaryPayDetails<IUkSummaryPayDetail>;
  previousValues?: IPreviousSummaryValues<IUkFlatSummaryLine>;
}

// endregion --SummaryItems--

// region --BreakageTotals--
export interface UKPayFactorTotals {
  breakageOrdinal: number;
  payCodeNumber: string;
  payCodeName: string;
  payFactorName: string;
  payDescription: string;
  totalDuration: number; // in minutes
  totalAmount: number; // total amount paid for this pay factor
}
export interface UkBreakageTotals extends IBreakageTotals {
  payFactorTotals: UKPayFactorTotals[];
}

// endregion --BreakageTotals--

export type UkFlatShift = IBaseFlatShift<AccountCode, string, IUkBreakage>;

export interface UkSummaryLine extends UkSummaryItems {
  summaryId: string;
}
